import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import AppDataGrid from "../../components/AppDataGrid";
import { BASE_URL } from "../../utils";
import { FaPlus, FaMinus } from "react-icons/fa";
import { FiEye, FiPaperclip, FiCopy, FiEdit2, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";

ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

const TABS = [
  { key: "all", label: "All Violations", endpoint: "all" },
  { key: "unsafe", label: "Unsafe Buildings", endpoint: "unsafe" },
  { key: "vacate", label: "Vacate Orders", endpoint: "vacate" },
  { key: "nuclear", label: "Nuclear / High Balance", endpoint: "nuclear" },
];

const BORO_OPTIONS = [
  { label: "All boroughs", value: "" },
  { label: "Manhattan", value: "1" },
  { label: "Bronx", value: "2" },
  { label: "Brooklyn", value: "3" },
  { label: "Queens", value: "4" },
  { label: "Staten Island", value: "5" },
];

const SOURCE_OPTIONS = [
  { label: "All sources", value: "" },
  { label: "DOB", value: "DOB" },
  { label: "ECB", value: "ECB" },
  { label: "HPD", value: "HPD" },
  { label: "HMC", value: "HMC" },
  { label: "DOF", value: "DOF" },
  { label: "VAC", value: "VAC" },
];

const OWNER_TYPE_OPTIONS = [
  { label: "All owner types", value: "" },
  { label: "Individual", value: "Individual" },
  { label: "Estate / Trust", value: "Estate / Trust" },
  { label: "Funds / Partnership", value: "Funds / Partnership" },
  { label: "LLC / Corporation", value: "LLC / Corporation" },
  { label: "Bank", value: "Bank" },
  { label: "Government", value: "Government" },
  { label: "Unknown", value: "Unknown" },
];

const STATUS_OPTIONS = [
  { label: "All status", value: "" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
  { label: "Pending", value: "Pending" },
  { label: "N/A", value: "N/A" },
];

const formatMoney = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("MM/DD/YYYY") : "-";
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("MMM D, YYYY h:mm A") : "-";
};

const formatBbl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  if (raw.includes("-")) return raw;
  if (raw.length === 10) return `${raw[0]}-${raw.slice(1, 6)}-${raw.slice(6, 10)}`;
  return raw;
};

const balanceClass = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "badge-pill badge-neutral";
  if (num >= 50000) return "badge-pill badge-rose";
  if (num >= 10000) return "badge-pill badge-amber";
  if (num >= 1000) return "badge-pill badge-amber";
  return "badge-pill badge-neutral";
};

const ownerTypeClass = (value) => {
  const v = String(value || "").toLowerCase();
  if (v.includes("individual")) return "badge-pill badge-green";
  if (v.includes("estate") || v.includes("trust")) return "badge-pill badge-amber";
  if (v.includes("fund") || v.includes("partnership")) return "badge-pill badge-amber";
  if (v.includes("llc") || v.includes("corporation")) return "badge-pill badge-neutral";
  if (v.includes("bank")) return "badge-pill badge-neutral";
  if (v.includes("government")) return "badge-pill badge-rose";
  return "badge-pill badge-neutral";
};

const leadTierClass = (value) => {
  const v = String(value || "").toUpperCase();
  if (v === "NUCLEAR") return "badge-pill badge-rose";
  if (v === "HOT") return "badge-pill badge-amber";
  return "badge-pill badge-green";
};

const statusClass = (value) => {
  const v = String(value || "").toLowerCase();
  if (v.includes("approved")) return "badge-pill badge-green";
  if (v.includes("reject")) return "badge-pill badge-rose";
  if (v.includes("pending")) return "badge-pill badge-amber";
  if (v.includes("processing")) return "badge-pill badge-amber";
  return "badge-pill badge-neutral";
};

const labelClass = (value) => {
  const v = String(value || "").toUpperCase();
  if (v === "HOT") return "ag-label-pill ag-label-pill--hot";
  if (v === "WARM") return "ag-label-pill ag-label-pill--warm";
  if (v === "COLD") return "ag-label-pill ag-label-pill--cold";
  return "ag-label-pill ag-label-pill--na";
};

const truncateText = (value, size = 42) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  if (text.length <= size) return text;
  return `${text.slice(0, size)}...`;
};

const stripHtml = (html) => {
  if (!html) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || "";
  } catch {
    return String(html).replace(/<[^>]+>/g, "");
  }
};

const sanitizeHtmlForView = (html) => {
  if (!html) return "";
  let s = String(html).replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
  s = s.replace(/\son\w+="[^"]*"/gi, "");
  s = s.replace(/\son\w+='[^']*'/gi, "");
  return s;
};

const isComplexHtml = (html) => {
  if (!html) return false;
  const lowered = String(html).toLowerCase();
  const tagCount = (String(html).match(/<\w+/g) || []).length;
  if (tagCount > 10) return true;
  if (lowered.includes("startfragment")) return true;
  if (lowered.includes("<div") || lowered.includes("<table") || lowered.includes("<img")) return true;
  if (String(html).length > 1000) return true;
  return false;
};

const firstNWords = (text, n = 5) => {
  if (!text) return "";
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  return words.slice(0, n).join(" ");
};

const resolveViolationStatus = (row) => {
  const notes = Array.isArray(row?.notes) ? row.notes : [];
  const latestNote = notes.reduce((acc, note) => {
    if (!note) return acc;
    if (!acc) return note;
    const aTime = dayjs(note.updated_at || note.created_at || note.followUp_date || 0).valueOf();
    const bTime = dayjs(acc.updated_at || acc.created_at || acc.followUp_date || 0).valueOf();
    return aTime >= bTime ? note : acc;
  }, null);

  const noteStatus = latestNote?.status || row?.note_status || row?.latest_status || row?.status || row?.current_status;
  const normalized = String(noteStatus || "").trim().toLowerCase();

  if (notes.length === 0) return "Processing";
  if (normalized.includes("approved")) return "Approved";
  if (normalized.includes("reject")) return "Rejected";
  if (normalized.includes("pending")) return "Pending";
  if (normalized.includes("processing")) return "Processing";
  return noteStatus ? String(noteStatus) : "Processing";
};

const resolveViolationLabel = (row) =>
  String(row?.latest_label || row?.label || row?.latest_tag || "NA").toUpperCase() || "NA";

async function copyToClipboard(text, successMsg = "Copied", failMsg = "Copy failed") {
  const payload = text == null ? "" : String(text);

  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(payload);
      toast.success(successMsg);
      return true;
    } catch (err) {
      console.warn("clipboard.writeText failed, fallback:", err);
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = payload;
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    textarea.style.width = "1px";
    textarea.style.height = "1px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (ok) {
      toast.success(successMsg);
      return true;
    }

    toast.error(failMsg);
    return false;
  } catch (err) {
    console.error("Fallback copy failed:", err);
    toast.error(failMsg);
    return false;
  }
}

function buildPdfHref(pdfUrl) {
  if (!pdfUrl) return null;
  const s = String(pdfUrl).trim();
  if (!s) return null;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return `${BASE_URL}${s}`;
  return `${BASE_URL}/${s}`;
}

export default function ViolationsV2Properties() {
  const gridRef = useRef(null);

  const [tab, setTab] = useState("all");
  const [filters, setFilters] = useState({
    borough: "",
    bbl: "",
    owner: "",
    address: "",
    source: "",
    ownerType: "",
    typeCode: "",
    minBalance: "",
    maxBalance: "",
    search: "",
    startDate: "",
    endDate: "",
    status: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    borough: "",
    bbl: "",
    owner: "",
    address: "",
    source: "",
    ownerType: "",
    typeCode: "",
    minBalance: "",
    maxBalance: "",
    search: "",
    startDate: "",
    endDate: "",
    status: "",
  });

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [sortField, setSortField] = useState("updated_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedCells, setExpandedCells] = useState({});
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const editEditorRef = useRef(null);

  const activeTab = useMemo(() => TABS.find((t) => t.key === tab) || TABS[0], [tab]);

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleEnterApply = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    applyFilters();
  };

  const clearFilters = () => {
    const cleared = {
      borough: "",
      bbl: "",
      owner: "",
      address: "",
      source: "",
      ownerType: "",
      typeCode: "",
      minBalance: "",
      maxBalance: "",
      search: "",
      startDate: "",
      endDate: "",
      status: "",
    };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const loadRows = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(`${BASE_URL}/api/violations-v2/${activeTab.endpoint}`, {
        params: {
          ...appliedFilters,
          page: pagination.page,
          limit: pagination.limit,
          sortField,
          sortDirection,
        },
      });

      setRows(Array.isArray(res.data?.data) ? res.data.data : []);

      const nextPagination = res.data?.pagination || {};
      setPagination((prev) => ({
        ...prev,
        page: Number(nextPagination.page || prev.page || 1),
        limit: Number(nextPagination.limit || prev.limit || 25),
        total: Number(nextPagination.total || 0),
        totalPages: Number(nextPagination.totalPages || 1),
      }));

      setSelectedRows([]);
    } catch (err) {
      console.error("V2 properties fetch error", err);
      setRows([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        totalPages: 1,
      }));
      setError("Failed to load violations-v2 properties.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    
  }, [activeTab.endpoint, appliedFilters, pagination.page, pagination.limit, sortField, sortDirection]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
  }, [tab]);

  const handleDeleteSelected = async () => {
    if (!selectedRows.length) return;

    const ids = selectedRows.map((row) => row.id).filter(Boolean);
    if (!ids.length) return;

    try {
      setLoading(true);
      await axios.delete(`${BASE_URL}/api/violations-v2/bulk-delete`, {
        data: { ids },
      });
      await loadRows();
    } catch (err) {
      console.error("Delete selected error", err);
      setError("Failed to delete selected records.");
    } finally {
      setLoading(false);
    }
  };

  const showHtmlContent = (html) => {
    setHtmlModalContent(html || "");
    setHtmlModalOpen(true);
  };

  const openEditNote = (note, bbl) => {
    setEditingNote({ ...(note || {}), bbl });
    setEditModalOpen(true);
    setTimeout(() => {
      if (editEditorRef.current) {
        editEditorRef.current.innerHTML = note?.note || "";
        editEditorRef.current.focus();
      }
    }, 50);
  };

  const handleSaveEditNote = async () => {
    if (!editingNote?.id || !editingNote?.bbl) return;
    const html = editEditorRef.current ? editEditorRef.current.innerHTML : editingNote.note || "";
    if (!String(html || "").trim()) {
      toast.error("Note cannot be empty.");
      return;
    }

    setEditSaving(true);
    try {
      await axios.put(
        `${BASE_URL}/api/violations-v2/${encodeURIComponent(editingNote.bbl)}/notes/${editingNote.id}`,
        {
          note: html,
          plain_text: stripHtml(html),
          pdf_url: editingNote.pdf_url || null,
          status: editingNote.status || "Processing",
          sub_status: editingNote.sub_status || "NA",
          label: editingNote.label || "NA",
          followUp_date: editingNote.followUp_date || null,
        }
      );
      setEditModalOpen(false);
      setEditingNote(null);
      await loadRows();
    } catch (err) {
      console.error("Error saving violation note:", err);
      toast.error("Failed to save note.");
    } finally {
      setEditSaving(false);
    }
  };

  const toggleDetail = useCallback((params) => {
    const api = params?.api;
    const node = params?.node;
    const rowId = node?.data?.id;
    if (!api || !node) return;

    const willOpen = !(node && node.expanded);
    api.forEachNode((n) => {
      if (n && n.expanded) n.setExpanded(false);
    });

    if (willOpen) {
      node.setExpanded(true);
      setExpandedRowId(rowId || null);
    } else {
      setExpandedRowId(null);
    }

    api.refreshCells({ force: true });
    api.resetRowHeights();
  }, []);

  const NoteItem = ({ note, bbl, onRefresh }) => {
    const raw = note?.note || note?.plain_text || "";
    const plain = stripHtml(raw);
    const complex = isComplexHtml(raw);
    const pdfHref = buildPdfHref(note?.pdf_url || note?.pdfUrl || "");
    const noteStatus = note?.status || "Processing";

    return (
      <div
        className={`text-sm mb-2 pl-2 border-l-4 ${
          String(noteStatus).toLowerCase().includes("approved")
            ? "border-green-500"
            : "border-rose-500"
        }`}
      >
        {raw ? (
          <>
            <div
              className="text-gray-800 whitespace-pre-line"
              dangerouslySetInnerHTML={{ __html: raw }}
            />
            {pdfHref ? (
              <div className="mt-1 mb-1 flex items-center">
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 underline flex items-center gap-1"
                >
                  <FiPaperclip size={12} /> <span>View Attached PDF</span>
                </a>
              </div>
            ) : null}
            <div className="text-[11px] text-gray-500 mt-1 flex items-center justify-between">
              <span>
                {note.created_at ? new Date(note.created_at).toLocaleString() : ""}
              </span>
              <div className="flex gap-3 items-center">
                <button
                  type="button"
                  onClick={() => openEditNote(note, bbl)}
                  className="text-indigo-600 text-xs inline-flex items-center gap-1"
                >
                  <FiEdit2 size={13} /> Edit
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm("Delete this note?")) return;
                    try {
                      await axios.delete(
                        `${BASE_URL}/api/violations-v2/${encodeURIComponent(bbl)}/notes/${note.id}`
                      );
                      toast.success("Note deleted");
                      onRefresh?.();
                    } catch (err) {
                      console.error("Delete violation note error", err);
                      toast.error("Failed to delete note.");
                    }
                  }}
                  className="text-red-500 text-xs inline-flex items-center gap-1"
                >
                  <FiTrash2 size={13} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-sm text-[var(--fg-muted)]">No note text.</div>
        )}
      </div>
    );
  };

  const DetailCellRenderer = ({ data }) => {
    const row = data || {};
    const notes = Array.isArray(row.notes) ? row.notes : [];
    const status = resolveViolationStatus(row);
    const label = resolveViolationLabel(row);

    return (
      <div
        className="p-3 border-t"
        style={{
          whiteSpace: "normal",
          background: "var(--surface)",
          color: "var(--fg)",
          borderColor: "var(--border)",
        }}
      >
        <div className="mb-2 text-xs">
          <strong>Sub-Status:</strong> {row?.latest_sub_status || row?.sub_status || "NA"}
        </div>
        <div className="mb-2 text-xs">
          <strong>Remark:</strong> {row?.remark || "NA"}
        </div>
        <div className="text-xs font-semibold mb-1">Notes:</div>

        <div className="mt-1">
          {notes.length ? (
            notes.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                bbl={row.bbl}
                onRefresh={() => loadRows()}
              />
            ))
          ) : (
            <div className="rounded-lg border border-dashed border-[var(--border)] p-4 text-sm text-[var(--fg-muted)]">
              No notes available for this record.
            </div>
          )}
        </div>
      </div>
    );
  };

 const columnDefs = useMemo(
  () => [
    {
      headerName: "",
      field: "__select",
      width: 46,
      minWidth: 46,
      maxWidth: 46,
      pinned: "left",
      sortable: false,
      filter: false,
      suppressMenu: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      resizable: false,
      cellClass: "text-center",
    },
    {
      headerName: "BBL",
      field: "bbl",
      width: 120,
      minWidth: 110,
      sortable: true,
      filter: false,
      cellRenderer: (params) => {
        const value = formatBbl(params.value);
        return (
          <Link
            to={`/violations-v2/${value}`}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline whitespace-nowrap"
          >
            {value}
          </Link>
        );
      },
      cellStyle: { whiteSpace: "nowrap" },
    },
    {
      headerName: "Case ID",
      field: "case_id",
      width: 130,
      minWidth: 120,
      sortable: true,
    },
    {
      headerName: "Borough",
      field: "borough_name",
      width: 120,
      minWidth: 110,
      sortable: true,
    },
    {
      headerName: "Address",
      field: "address",
      flex: 1,
      minWidth: 220,
      cellRenderer: (p) => <span title={p.value || ""}>{p.value || "-"}</span>,
      tooltipField: "address",
    },
    {
      headerName: "Owner / Respondent",
      field: "owner_name",
      flex: 1,
      minWidth: 220,
      cellRenderer: (p) => <span title={p.value || ""}>{p.value || "-"}</span>,
      tooltipField: "owner_name",
    },
    {
      headerName: "Owner Type",
      field: "owner_type",
      width: 150,
      minWidth: 130,
      sortable: true,
      cellRenderer: (p) => (
        <span className={ownerTypeClass(p.value)}>
          {p.value || "Unknown"}
        </span>
      ),
    },
    {
      headerName: "Violation No.",
      field: "violation_number",
      width: 150,
      minWidth: 130,
      sortable: true,
    },
    {
      headerName: "Type",
      field: "violation_type",
      width: 160,
      minWidth: 140,
      sortable: true,
      cellRenderer: (p) => (
        <span title={p.value || ""}>{truncateText(p.value, 26)}</span>
      ),
    },
    {
      headerName: "Classification",
      field: "classification",
      width: 160,
      minWidth: 140,
      sortable: true,
      cellRenderer: (p) => (
        <span title={p.value || ""}>{truncateText(p.value, 26)}</span>
      ),
    },
    {
      headerName: "Latest Status",
      field: "latest_status",
      width: 170,
      minWidth: 160,
      sortable: false,
      valueGetter: (p) =>
        p.data?.latest_status ||
        p.data?.status ||
        resolveViolationStatus(p.data),
      cellRenderer: (p) => {
        const isExpanded = !!p.node?.expanded;
        const value =
          p.data?.latest_status ||
          p.data?.status ||
          resolveViolationStatus(p.data);

        return (
          <div className="w-full flex items-center justify-center gap-2">
            <span className={statusClass(value)}>{value}</span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDetail(p);
              }}
              className={[
                "w-6 h-6 rounded-full flex items-center justify-center",
                "border border-[var(--border)] shadow-sm transition",
                isExpanded
                  ? "bg-rose-600 text-white hover:bg-rose-700"
                  : "bg-[var(--accent)] text-white hover:bg-[var(--accent-strong)]",
              ].join(" ")}
              title={isExpanded ? "Collapse" : "Expand"}
              type="button"
            >
              {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
            </button>
          </div>
        );
      },
    },

    {
      headerName: "Last Note",
      field: "latest_note",
      minWidth: 280,
      width: 320,
      sortable: false,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "16px",
        overflow: "visible",
        textOverflow: "clip",
        wordBreak: "break-word",
        display: "flex",
        alignItems: "center",
      },
      cellRenderer: (p) => {
        const row = p.data || {};
        const raw =
          row.latest_plain_text ||
          row.latest_note ||
          row.last_note ||
          p.value ||
          "";

        const htmlRaw = row.latest_note || row.last_note || "";
        const plain = stripHtml(raw);
        const complex = isComplexHtml(htmlRaw);
        const key = `${row.id || row.bbl || row.case_id || "row"}-latest_note`;

        const expanded = !!expandedCells[key];
        const pdfHref = buildPdfHref(
          row?.latest_pdf_url ||
            row?.latest_note_pdf_url ||
            row?.note_pdf_url ||
            row?.pdf_url ||
            row?.attached_pdf_url ||
            null
        );

        const words = plain.trim().split(/\s+/).filter(Boolean);
        const longEnough = words.length > 5;
        const preview = longEnough
          ? words.slice(0, 5).join(" ") + "..."
          : words.join(" ");

        const shown = expanded ? plain || htmlRaw : preview || "No note";

        return (
          <div
            className="w-full flex flex-col items-start justify-center py-1"
            style={{
              minHeight: expanded ? "auto" : 52,
              overflow: "visible",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            <div className="w-full text-[11px] leading-4 mb-1 whitespace-pre-wrap break-words">
              {shown}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {longEnough && (
                <button
                  type="button"
                  className="text-[10px] text-[var(--accent)]"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setExpandedCells((prev) => ({
                      ...prev,
                      [key]: !expanded,
                    }));

                    requestAnimationFrame(() => {
                      try {
                        p.api.refreshCells({
                          rowNodes: [p.node],
                          columns: [p.column],
                          force: true,
                        });
                        p.api.resetRowHeights();
                        p.api.onRowHeightChanged?.();
                      } catch {}
                    });
                  }}
                >
                  {expanded ? "View Less" : "View More"}
                </button>
              )}

              {complex && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    showHtmlContent(htmlRaw);
                  }}
                  title="View full HTML"
                  className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                  type="button"
                >
                  <FiEye size={15} className="text-[var(--accent)]" />
                </button>
              )}

              {pdfHref && (
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title="Open attached PDF"
                  className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                >
                  <FiPaperclip size={15} className="text-blue-600" />
                </a>
              )}

              {raw && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(
                      plain || htmlRaw,
                      "Copied plain text",
                      "Copy failed"
                    );
                  }}
                  title="Copy plain text"
                  className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
                  type="button"
                >
                  <FiCopy size={14} className="text-[var(--fg)]" />
                </button>
              )}
            </div>
          </div>
        );
      },
    },

    {
      headerName: "Notes Updated",
      field: "latest_note_updated_at",
      width: 150,
      minWidth: 140,
      sortable: true,
      valueGetter: (p) =>
        p.data?.latest_note_updated_at ||
        p.data?.latest_note_created_at ||
        p.data?.notes?.[0]?.updated_at ||
        p.data?.notes?.[0]?.created_at ||
        null,
      cellRenderer: (p) => {
        const value =
          p.data?.latest_note_updated_at ||
          p.data?.latest_note_created_at ||
          p.data?.notes?.[0]?.updated_at ||
          p.data?.notes?.[0]?.created_at;

        return (
          <div className="w-full text-center text-xs leading-4">
            {value ? (
              <>
                <div>{dayjs(value).format("MMM D, YYYY")}</div>
                <div className="text-[10px] text-[var(--fg-muted)]">
                  ({dayjs(value).fromNow()})
                </div>
              </>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },

    {
      headerName: "Sub Status",
      field: "latest_sub_status",
      width: 150,
      minWidth: 140,
      sortable: false,
      valueGetter: (p) =>
        p.data?.latest_sub_status ||
        p.data?.sub_status ||
        p.data?.notes?.[0]?.sub_status ||
        "NA",
      cellRenderer: (p) => {
        const value =
          p.data?.latest_sub_status ||
          p.data?.sub_status ||
          p.data?.notes?.[0]?.sub_status ||
          "NA";

        return (
          <div
            className="w-full text-xs text-center whitespace-pre-wrap"
            style={{ wordBreak: "break-word", lineHeight: "16px" }}
            title={value}
          >
            {value}
          </div>
        );
      },
    },

    {
      headerName: "Remark",
      field: "latest_remark",
      width: 160,
      minWidth: 150,
      sortable: false,
      valueGetter: (p) =>
        p.data?.latest_remark ||
        p.data?.remark ||
        p.data?.notes?.[0]?.remark ||
        "NA",
      cellRenderer: (p) => {
        const value =
          p.data?.latest_remark ||
          p.data?.remark ||
          p.data?.notes?.[0]?.remark ||
          "NA";

        return (
          <div
            className="w-full text-xs text-center whitespace-pre-wrap"
            style={{ wordBreak: "break-word", lineHeight: "16px" }}
            title={value}
          >
            {value}
          </div>
        );
      },
    },

    {
      headerName: "Label",
      field: "latest_label",
      width: 130,
      minWidth: 120,
      sortable: false,
      valueGetter: (p) =>
        p.data?.latest_label ||
        p.data?.label ||
        p.data?.notes?.[0]?.label ||
        "NA",
      cellRenderer: (p) => {
        const value = String(
          p.data?.latest_label ||
            p.data?.label ||
            p.data?.notes?.[0]?.label ||
            "NA"
        ).toUpperCase();

        return (
          <div className="w-full flex items-center justify-center">
            <span className={labelClass(value)}>{value}</span>
          </div>
        );
      },
    },

    {
      headerName: "Data Updated On",
      field: "latest_updated_at",
      width: 150,
      minWidth: 140,
      sortable: true,
      valueGetter: (p) =>
        p.data?.latest_updated_at ||
        p.data?.updated_at ||
        p.data?.created_at ||
        null,
      cellRenderer: (p) => {
        const value =
          p.data?.latest_updated_at ||
          p.data?.updated_at ||
          p.data?.created_at;

        return (
          <div className="w-full text-center text-xs">
            {value ? dayjs(value).format("MMM D, YYYY") : "-"}
          </div>
        );
      },
    },

    

    {
      headerName: "Lead Score",
      field: "lead_score",
      width: 110,
      minWidth: 100,
      sortable: true,
    },
    {
      headerName: "Lead Tier",
      field: "lead_tier",
      width: 110,
      minWidth: 100,
      sortable: true,
      cellRenderer: (p) => (
        <span className={leadTierClass(p.value)}>
          {p.value || "WARM"}
        </span>
      ),
    },
    {
      headerName: "Total",
      field: "total_violations",
      width: 90,
      minWidth: 80,
      sortable: true,
    },
    {
      headerName: "Balance Due",
      field: "total_balance_due",
      width: 130,
      minWidth: 120,
      sortable: true,
      cellRenderer: (p) => (
        <span className={balanceClass(p.value)}>
          {formatMoney(p.value)}
        </span>
      ),
    },
    {
      headerName: "Latest Issue",
      field: "latest_issue_date",
      width: 120,
      minWidth: 110,
      sortable: true,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      headerName: "Created",
      field: "created_at",
      width: 120,
      minWidth: 110,
      sortable: true,
      valueFormatter: (p) => formatDate(p.value),
    },
  ],
  [expandedCells, toggleDetail]
);

  const onGridReady = (params) => {
    gridRef.current = params.api;
  };

  const onSortChanged = (params) => {
    const model = params.api.getSortModel ? params.api.getSortModel() : [];
    if (!model || model.length === 0) return;
    const next = model[0];
    if (!next?.colId || !next?.sort) return;

    setSortField(next.colId);
    setSortDirection(next.sort);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const onSelectionChanged = (params) => {
    const selected = params.api?.getSelectedRows?.() || [];
    setSelectedRows(selected);
  };

  return (
    <div
      className="p-2 md:p-3 lg:p-4 space-y-3 w-full max-w-none"
      style={{
        "--control-height": "28px",
        "--control-font": "10px",
        "--control-pad-x": "10px",
        "--control-pad-y": "3px",
      }}
    >
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/85 shadow-[0_20px_60px_rgba(15,23,42,0.10)] w-full">
        <div
          className="absolute inset-0 opacity-80 bg-gradient-to-r from-indigo-100/60 via-cyan-100/50 to-transparent"
          aria-hidden
        />
        <div className="relative px-3 py-3 md:px-4 md:py-4 lg:px-5 lg:py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-500 flex items-center justify-center text-[15px] font-bold text-white shadow-lg shadow-indigo-500/25">
                V
              </div>
              <div className="leading-tight">
                <p className="page-kicker">Web Platform</p>
                <h1 className="page-title">Violations Properties</h1>
                <p className="page-subtitle">
                  Properties and latest note status grouped for violations.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`ui-pill transition-colors ${
                    tab === t.key
                      ? "bg-[var(--accent)] text-white border-[var(--accent-strong)] shadow-[var(--shadow-soft)]"
                      : "bg-[var(--surface)] text-[var(--fg-strong)] border-[var(--border)] hover:bg-[var(--surface-muted)]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="filter-panel p-3">
        <div className="filter-grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
          <input
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search owner, address, BBL, case id"
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <select
            value={filters.borough}
            onChange={(e) => setFilters((f) => ({ ...f, borough: e.target.value }))}
            className="ui-select"
          >
            {BORO_OPTIONS.map((o) => (
              <option key={o.value || o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <input
            value={filters.bbl}
            onChange={(e) => setFilters((f) => ({ ...f, bbl: e.target.value }))}
            placeholder="BBL"
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <input
            value={filters.owner}
            onChange={(e) => setFilters((f) => ({ ...f, owner: e.target.value }))}
            placeholder="Owner / Respondent"
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <select
            value={filters.ownerType}
            onChange={(e) => setFilters((f) => ({ ...f, ownerType: e.target.value }))}
            className="ui-select"
          >
            {OWNER_TYPE_OPTIONS.map((o) => (
              <option key={o.value || o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <input
            value={filters.address}
            onChange={(e) => setFilters((f) => ({ ...f, address: e.target.value }))}
            placeholder="Address"
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <select
            value={filters.source}
            onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
            className="ui-select"
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value || o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <input
            value={filters.typeCode}
            onChange={(e) => setFilters((f) => ({ ...f, typeCode: e.target.value }))}
            placeholder="Type / classification"
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <input
            value={filters.minBalance}
            onChange={(e) => setFilters((f) => ({ ...f, minBalance: e.target.value }))}
            placeholder="Min balance"
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <input
            value={filters.maxBalance}
            onChange={(e) => setFilters((f) => ({ ...f, maxBalance: e.target.value }))}
            placeholder="Max balance"
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="ui-select"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
            className="ui-input"
            onKeyDown={handleEnterApply}
          />

          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
            className="ui-input"
            onKeyDown={handleEnterApply}
          />
        </div>

        <div className="filter-actions flex flex-wrap gap-2">
          <button onClick={applyFilters} className="ui-btn ui-btn-primary">
            Apply
          </button>
          <button onClick={clearFilters} className="ui-btn">
            Clear
          </button>

          {selectedRows.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="ui-btn bg-rose-600 text-white border-rose-600 hover:bg-rose-700"
            >
              Delete Selected ({selectedRows.length})
            </button>
          )}
        </div>
      </div>

      <div className="surface-card rounded-2xl p-2">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">{activeTab.label}</div>
          <div className="text-xs text-[var(--fg-muted)]">
            Showing {rows.length} of {pagination.total}
          </div>
        </div>

        {error && <div className="text-sm text-rose-600 mb-3">{error}</div>}

        <div className="w-full min-w-0">
          <div className="rounded-2xl border border-[var(--border)] shadow-sm bg-[var(--surface)] overflow-hidden">
            <div className="w-full min-w-0">
              <AppDataGrid
                gridRef={gridRef}
                className="w-full ag-v2-grid"
                rowData={rows}
                columnDefs={columnDefs}
                loading={loading}
                height={"calc(100vh - 260px)"}
                minWidth={0}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  filter: false,
                  minWidth: 90,
                  cellClass: "text-sm text-center",
                  headerClass: "text-center",
                  cellStyle: {
                    userSelect: "text",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                rowHeight={52}
                headerHeight={36}
                onGridReady={onGridReady}
                onSortChanged={onSortChanged}
                onSelectionChanged={onSelectionChanged}
                rowSelection="multiple"
                suppressRowClickSelection={true}
                alwaysShowVerticalScroll={true}
                alwaysShowHorizontalScroll={true}
                masterDetail={true}
                isRowMaster={() => true}
                detailCellRenderer={DetailCellRenderer}
                detailRowAutoHeight={true}
                detailRowHeight={260}
                overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data...</span>'
                overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No records found.</span>'
              />
            </div>
          </div>
        </div>

        <div className="pagination-bar flex items-center gap-2 mt-4">
          <button
            disabled={pagination.page <= 1}
            onClick={() => setPagination((p) => ({ ...p, page: 1 }))}
          >
            First
          </button>

          <button
            disabled={pagination.page <= 1}
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: Math.max(1, p.page - 1),
              }))
            }
          >
            Prev
          </button>

          <span className="text-sm">
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: Math.min(p.totalPages, p.page + 1),
              }))
            }
          >
            Next
          </button>

          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: p.totalPages,
              }))
            }
          >
            Last
          </button>

          <select
            value={pagination.limit}
            onChange={(e) =>
              setPagination((p) => ({
                ...p,
                limit: Number(e.target.value),
                page: 1,
              }))
            }
            className="ml-auto ui-select"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-3xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Edit Note</h3>
              <div className="text-xs text-gray-500">
                {editingNote?.created_at
                  ? new Date(editingNote.created_at).toLocaleString()
                  : ""}
              </div>
            </div>

            <div
              ref={editEditorRef}
              contentEditable
              suppressContentEditableWarning
              className="border rounded p-2 mb-3 text-sm"
              style={{ minHeight: 180, maxHeight: 400, overflowY: "auto" }}
            />

            {editingNote?.pdf_url || editingNote?.pdfUrl ? (
              <div className="mb-3">
                <a
                  href={buildPdfHref(
                    editingNote.pdf_url || editingNote.pdfUrl,
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline text-sm inline-flex items-center gap-2"
                >
                  <FiPaperclip size={14} /> View Attached PDF
                </a>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingNote(null);
                }}
                className="px-3 py-1 border rounded text-sm"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditNote}
                disabled={editSaving}
                className={`px-4 py-2 rounded text-white ${
                  editSaving
                    ? "bg-gray-400"
                    : "bg-green-600 hover:bg-green-700"
                }`}
                type="button"
              >
                {editSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {htmlModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center pt-20 bg-black bg-opacity-40 p-4 overflow-y-auto">
            <div className="bg-white rounded shadow-lg w-full max-w-4xl  p-4 max-h-[80vh] overflow-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">
                  Last Note — Full HTML
                </h3>
                <button
                  onClick={() => setHtmlModalOpen(false)}
                  className="px-2 py-1 border rounded text-sm"
                  type="button"
                >
                  Close
                </button>
              </div>

              <div className="prose max-w-full text-sm ">
                <div
                  className="bg-[var(--surface-muted)] text-[var(--fg-muted)]"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtmlForView(htmlModalContent),
                  }}
                />
              </div>

              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() =>
                    copyToClipboard(
                      stripHtml(htmlModalContent),
                      "Copied plain text",
                      "Failed to copy",
                    )
                  }
                  className="px-3 py-1 border rounded text-sm"
                  type="button"
                >
                  Copy plain text
                </button>

                <button
                  onClick={() =>
                    copyToClipboard(
                      htmlModalContent,
                      "Copied raw HTML",
                      "Failed to copy",
                    )
                  }
                  className="px-3 py-1 border rounded text-sm"
                  type="button"
                >
                  Copy raw HTML
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}    </div>
  );
}
